import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, ArrowRight, Trash2, Inbox } from "lucide-react";
import { getAllFavorites, toggleFavoritePassage } from "../lib/dailyJourney";
import ShareButton from "../features/share/ShareButton";

export default function Favoritos() {
  const [favs, setFavs] = useState({ devotionals: [], prayers: [], passages: [] });

  useEffect(() => {
    setFavs(getAllFavorites());
  }, []);

  const handleRemove = (ref, href) => {
    toggleFavoritePassage({ ref, href });
    setFavs(getAllFavorites());
    toast("Removido dos favoritos.");
  };

  const passages = favs.passages || [];

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="favoritos-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="favoritos-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Biblioteca pessoal</p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Meus Versículos</h1>
        <p className="text-navy/70 mt-3 text-sm max-w-xl">
          Passagens que você salvou para revisitar. Salvos apenas neste dispositivo.
        </p>
      </div>

      {passages.length === 0 ? (
        <div
          className="bg-[hsl(var(--parchment))] border border-border rounded-2xl p-10 text-center"
          data-testid="favoritos-empty"
        >
          <Inbox className="w-8 h-8 text-gold mx-auto" />
          <p className="font-display text-xl text-navy mt-4">Nenhum versículo salvo ainda</p>
          <p className="text-navy/60 text-sm mt-2 max-w-sm mx-auto">
            Enquanto lê a Bíblia, toque em um versículo e selecione{" "}
            <span className="font-medium text-navy">Salvar</span> para encontrá-lo aqui.
          </p>
          <Link
            to="/biblia"
            className="inline-flex items-center gap-2 mt-6 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            data-testid="favoritos-empty-cta"
          >
            Abrir Bíblia <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="favoritos-list">
          {passages.map((p) => (
            <li
              key={p.ref}
              className="group bg-white border border-border rounded-2xl p-5 card-lift flex items-center gap-4"
              data-testid={`favoritos-item-${p.ref.replace(/\s+/g, "-").replace(/:/g, "-")}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
                <Bookmark className="w-5 h-5 text-gold" fill="currentColor" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-navy leading-tight">{p.ref}</p>
                {p.saved_at && (
                  <p className="text-[11px] text-navy/50 mt-0.5">
                    Salvo em {new Date(p.saved_at).toLocaleDateString("pt-BR")}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <div data-testid={`favoritos-share-slot-${p.ref.replace(/\s+/g, "-").replace(/:/g, "-")}`}>
                  <ShareButton
                    contentType="verse"
                    contentId={p.ref}
                    title={p.ref}
                    mainText={p.text || `${p.ref} · Palavra guardada.`}
                    verse={p.text || ""}
                    verseRef={p.ref}
                    theme="contemplacao"
                  />
                </div>
                {p.href && (
                  <Link
                    to={p.href}
                    aria-label={`Abrir ${p.ref}`}
                    data-testid={`favoritos-open-${p.ref.replace(/\s+/g, "-").replace(/:/g, "-")}`}
                    className="w-9 h-9 rounded-full bg-navy text-white hover:bg-gold hover:text-navy flex items-center justify-center transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(p.ref, p.href)}
                  aria-label={`Remover ${p.ref}`}
                  data-testid={`favoritos-remove-${p.ref.replace(/\s+/g, "-").replace(/:/g, "-")}`}
                  className="w-9 h-9 rounded-full bg-white border border-border text-navy/50 hover:text-red-500 hover:border-red-200 flex items-center justify-center transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
